<script setup lang="ts">
import { computed } from "vue";
import { marked } from "marked";
import type { IMarkedTextProps } from "./VMarkedText.types";
import { walkTokensAndRewriteDeepLinks } from "@magnit/core/src/components/VMarkedText/deeplinks";

const props = withDefaults(defineProps<IMarkedTextProps>(), {
  gfm: true,
  breaks: true,
  walkTokens: () => walkTokensAndRewriteDeepLinks,
});

const html = computed(() => {
  const { text, ...options } = props;
  return marked.parse(text, options);
});
</script>

<template>
  <div class="marked-text" v-html="html" />
</template>

<style lang="postcss" scoped>
.marked-text {
  :deep(h1) {
    font: var(--pl-font-headline-large);
    font-family: var(--pl-font-family-secondary);
    margin-top: var(--pl-unit-x4);
  }

  :deep(h2) {
    font: var(--pl-font-headline-medium);
    font-family: var(--pl-font-family-secondary);
    margin-top: var(--pl-unit-x4);
  }

  :deep(h3) {
    font: var(--pl-font-headline-small);
    font-family: var(--pl-font-family-secondary);
    margin-top: var(--pl-unit-x4);
  }

  :deep(h4) {
    font: var(--pl-font-title-large);
    font-family: var(--pl-font-family-secondary);
    margin-top: var(--pl-unit-x4);
  }

  :deep(h5) {
    font: var(--pl-font-title-medium);
    margin-top: var(--pl-unit-x4);
  }

  :deep(h6) {
    font: var(--pl-font-title-small);
    margin-top: var(--pl-unit-x4);
    margin-block-end: unset;
  }

  :deep(p) {
    font: var(--pl-font-body-large-regular);
    margin-top: var(--pl-unit-x1);
  }

  :deep(strong) {
    font-weight: 500;
  }

  :deep(a) {
    color: var(--pl-text-link-default);
    text-decoration: underline;

    &:visited {
      color: var(--pl-text-link-visited);
    }
  }

  :deep(ol) {
    list-style: decimal;
    padding-left: var(--pl-unit-x7);
    margin: var(--pl-unit-x2) 0;
  }

  :deep(ul) {
    list-style: initial;
    padding-left: var(--pl-unit-x7);
    margin: var(--pl-unit-x2) 0;
  }

  :deep(table) {
    border: 1px solid var(--pl-divider-primary);
    margin-bottom: var(--pl-unit-x5);
    border-collapse: collapse;

    th {
      font-weight: bold;
      padding: var(--pl-unit-x1);
      background: var(--pl-global-background-secondary);
      border: 1px solid var(--pl-divider-secondary);
    }

    td {
      padding: var(--pl-unit-x1) var(--pl-unit-x2);
      border: 1px solid var(--pl-divider-primary);
      text-align: left;
    }

    tbody tr:nth-child(odd) {
      background: var(--pl-global-background-primary);
    }

    tbody tr:nth-child(even) {
      background: var(--pl-global-background-secondary);
    }
  }
}
</style>
